import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';

interface LifecycleStep {
  index: string;
  label: string;
  description: string;
}

const STEPS: LifecycleStep[] = [
  {
    index: '01',
    label: 'Acquire',
    description: 'Secure a premium domain that actually fits your market, pricing, and authority level.',
  },
  {
    index: '02',
    label: 'Define',
    description: 'Clarify your offers, audience, and positioning so your site has something sharp and memorable to say.',
  },
  {
    index: '03',
    label: 'Build',
    description: 'Design and develop a conversion-first website and simple funnel that makes it easy to say "yes."',
  },
  {
    index: '04',
    label: 'Legitimize',
    description: 'Put the basics in place—entity, legal, HR, and professional tools—so you look and feel legitimate.',
  },
  {
    index: '05',
    label: 'Launch',
    description: 'Start real conversations, book consults, and refine based on the clients you actually want more of.',
  },
];

export function HomeLifecycle() {
  const lifecycleReveal = useRevealOnce({ amount: 0.25 });
  const headingReveal = useRevealOnce({ amount: 0.25 });

  return (
    <section id="lifecycle" className="relative w-full py-16 md:py-20 bg-slate-950 text-white overflow-hidden">
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute top-1/4 left-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
        aria-hidden="true"
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Heading and intro */}
          <motion.div
            ref={headingReveal.ref}
            className="text-center mb-12 md:mb-16"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.h2
              variants={getGridItemVariants(0)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-white"
            >
              Most domains just sit there and never earn their keep.
            </motion.h2>
            <motion.p
              variants={getGridItemVariants(1)}
              className="text-lg md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed"
            >
              Plenty of smart service providers buy a clever domain, stall on the build, and quietly lose months of leads to competitors who look more established than they really are. You don't need another half-finished website—you need a clear path from domain to booked calls.
            </motion.p>
          </motion.div>

          {/* Lifecycle step cards */}
          <motion.div
            ref={lifecycleReveal.ref}
            className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8 mb-12 md:mb-16"
            variants={gridContainerVariants}
            initial="hidden"
            animate={lifecycleReveal.controls}
          >
            {STEPS.map((step, index) => (
              <motion.div
                key={step.index}
                variants={getGridItemVariants(index)}
                className="group"
              >
                <div className="relative h-full bg-slate-900 border border-slate-800 rounded-xl p-6 md:p-8 transition-all duration-300 hover:scale-105 hover:border-slate-700 hover:shadow-lg hover:shadow-blue-500/10">
                  {/* Step index */}
                  <div className="absolute top-4 right-4 text-xs md:text-sm font-mono text-slate-600 group-hover:text-slate-500 transition-colors">
                    {step.index}
                  </div>

                  {/* Step content */}
                  <div className="pr-8">
                    <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-white">
                      {step.label}
                    </h3>
                    <p className="text-sm md:text-base text-slate-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Micro-CTA row */}
          <motion.div
            className="text-center"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.p
              variants={getGridItemVariants(2)}
              className="text-lg md:text-xl text-slate-300 mb-6 md:mb-8"
            >
              Start where you are:
            </motion.p>
            <motion.div
              variants={getGridItemVariants(3)}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6"
            >
              <Link
                to="/domains"
                className="text-base md:text-lg font-semibold text-white hover:text-blue-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 underline underline-offset-4 decoration-2 decoration-blue-500/50 hover:decoration-blue-400"
              >
                Step 1: Find your domain →
              </Link>
              <Link
                to="/services"
                className="text-base md:text-lg font-semibold text-white hover:text-blue-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 underline underline-offset-4 decoration-2 decoration-blue-500/50 hover:decoration-blue-400"
              >
                Already have a domain? View launch services →
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

